import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import claimsService from '../../services/claimService';
import { toast } from 'react-toastify';

/**
 * Composant d'évaluation principal 
 * Permet aux clients de noter leur expérience après clôture d'une réclamation
 */
const RatingComponent = () => {
    const { trackingNumber } = useParams();
    const navigate = useNavigate();

    // États
    const [hoveredRating, setHoveredRating] = useState(0);
    const [selectedRating, setSelectedRating] = useState(0);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [claimData, setClaimData] = useState(null);
    const [error, setError] = useState(null);

    // Récupérer les données de la réclamation au chargement
    useEffect(() => {
        const fetchClaimData = async () => {
            if (!trackingNumber) {
                setError("Numéro de suivi manquant");
                setInitialLoading(false);
                return;
            }

            try {
                setInitialLoading(true);
                const response = await claimsService.getClaimByTrackingNumber(trackingNumber);
                setClaimData(response);

                // Vérifier si la réclamation est clôturée
                if (response.status !== 'closed') {
                    setError("Cette réclamation n'est pas encore clôturée et ne peut pas être évaluée.");
                }

                // Vérifier si la réclamation a déjà été évaluée
                if (response.rating && response.rating.id) {
                    setSubmitted(true);
                    setSelectedRating(response.rating.rating); // Utilisez rating.rating au lieu de rating.score
                    setComment(response.rating.comment || '');
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des données:', error);
                setError("Impossible de récupérer les détails de la réclamation. Veuillez vérifier le numéro de suivi.");
            } finally {
                setInitialLoading(false);
            }
        };

        fetchClaimData();
    }, [trackingNumber]);

    // Gestion des interactions avec les étoiles
    const handleMouseOver = (rating) => {
        if (!submitted) setHoveredRating(rating);
    };

    const handleMouseLeave = () => {
        if (!submitted) setHoveredRating(0);
    };

    const handleClick = (rating) => {
        if (!submitted) setSelectedRating(rating);
    };

    // Soumission du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedRating === 0) {
            toast.error('Veuillez sélectionner une note');
            return;
        }

        try {
            setLoading(true);

            // Utiliser directement le numéro de suivi au lieu de l'ID
            await claimsService.rateClaimAsync(trackingNumber, {
                rating: selectedRating,
                comment: comment
            });

            setSubmitted(true);
            toast.success('Merci pour votre évaluation !');

            // Ne pas rediriger automatiquement, laisser l'utilisateur choisir
        } catch (error) {
            console.error('Erreur lors de la soumission de l\'évaluation:', error);
            toast.error(error.message || 'Une erreur est survenue lors de la soumission');
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour rediriger vers la page d'accueil
    const handleGoHome = () => {
        navigate('/');
    };

    // Fonction pour rediriger vers la page de suivi
    const handleGoToTracking = () => {
        // Rediriger vers la page de suivi avec le trackingNumber préchargé
        navigate(`/tracking/${trackingNumber}`);
    };

    // Générer le texte descriptif associé à la note
    const getRatingLabel = (rating) => {
        switch (rating) {
            case 1: return "Très insatisfait";
            case 2: return "Insatisfait";
            case 3: return "Neutre";
            case 4: return "Satisfait";
            case 5: return "Très satisfait";
            default: return "";
        }
    };

    // Affichage pendant le chargement initial
    if (initialLoading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3">Chargement des informations de la réclamation...</p>
            </div>
        );
    }

    // Affichage en cas d'erreur
    if (error) {
        return (
            <div className="container py-5">
                <div className="card border-danger">
                    <div className="card-header bg-danger text-white">
                        <h4>Erreur</h4>
                    </div>
                    <div className="card-body">
                        <p className="card-text">{error}</p>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleGoHome}
                            >
                                ← Retour à l'accueil
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleGoToTracking}
                            >
                                Voir la réclamation
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">Évaluation de la gestion de votre réclamation</h4>
                    <div className="small mt-1">
                        N° de suivi: {trackingNumber}
                    </div>
                </div>
                <div className="card-body">
                    {submitted ? (
                        // Confirmation après soumission
                        <div className="text-center py-4">
                            <div className="display-1 text-success mb-3">
                                ✓
                            </div>
                            <h3>Merci pour votre évaluation !</h3>
                            <p className="lead">Votre retour est précieux pour nous aider à améliorer nos services.</p>
                            <div className="my-4">
                                <div className="d-flex justify-content-center mb-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span
                                            key={star}
                                            style={{
                                                fontSize: '40px',
                                                color: star <= selectedRating ? '#FFC107' : '#E4E5E9',
                                                margin: '0 10px'
                                            }}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                                <div className="text-muted">
                                    {getRatingLabel(selectedRating)}
                                </div>
                                {comment && (
                                    <div className="card mt-3">
                                        <div className="card-body">
                                            <p className="card-text font-italic">"{comment}"</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex gap-3 justify-content-center mt-4">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGoToTracking}
                                >
                                    Voir ma réclamation
                                </button>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleGoHome}
                                >
                                    Retour à l'accueil
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Formulaire d'évaluation
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <p className="lead mb-3">Comment évalueriez-vous la gestion de votre réclamation ?</p>
                                <div className="d-flex justify-content-center mb-3">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span
                                            key={star}
                                            className="star-rating"
                                            style={{
                                                fontSize: '40px',
                                                cursor: 'pointer',
                                                color: star <= (hoveredRating || selectedRating) ? '#FFC107' : '#E4E5E9',
                                                margin: '0 10px'
                                            }}
                                            onMouseOver={() => handleMouseOver(star)}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={() => handleClick(star)}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                                <div className="text-center mb-3">
                                    <span className="text-muted">
                                        {getRatingLabel(hoveredRating || selectedRating)}
                                    </span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label htmlFor="comment" className="form-label">Vos commentaires (facultatif)</label>
                                <textarea
                                    id="comment"
                                    className="form-control"
                                    rows="4"
                                    placeholder="Partagez votre expérience ou vos suggestions..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                ></textarea>
                            </div>

                            <div className="d-flex justify-content-between mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleGoToTracking}
                                >
                                    ← Retour à ma réclamation
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || selectedRating === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Envoi en cours...
                                        </>
                                    ) : "Soumettre mon évaluation"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
                <div className="card-footer bg-light">
                    <small className="text-muted">
                        Votre évaluation nous aide à améliorer continuellement notre service de gestion des réclamations.
                    </small>
                </div>
            </div>
        </div>
    );
};

export default RatingComponent;